import { useState, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Minus, Plus, Trash2, Package, Scale, ChevronDown } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { formatPrice, formatWeight, getWeightOptions } from '@/shared/lib/format'
import { calculateTeaPrice, CAKE_WEIGHT } from '@/shared/lib/pricing'
import { Button } from '@/shared/ui/button'
import { ProductImage } from '@/entities/product/ui'
import { ProductWeightSelectorSheet } from '@/widgets/product-weight-selector-sheet'
import type { CartItem as CartItemType } from '../api/use-cart'
import { useUpdateCartItem, useUpdateCartItemWeight, useRemoveFromCart } from '../api/use-cart'
import { useTelegram } from '@/shared/lib/telegram'
import { useDeliveryStore } from '@/features/checkout/model'

// Доступные веса для чая определяются динамически
// Если текущий вес = 357 (блин), то показываем вариант блина

export interface CartItemProps {
  item: CartItemType
  onHapticFeedback?: (type?: 'light' | 'medium' | 'heavy') => void
}

/**
 * UI компонент: Элемент корзины
 */
export function CartItem({ item, onHapticFeedback }: CartItemProps) {
  const [isWeightSheetOpen, setIsWeightSheetOpen] = useState(false)
  const navigate = useNavigate()
  
  const updateCartItem = useUpdateCartItem()
  const updateCartItemWeight = useUpdateCartItemWeight()
  const removeFromCart = useRemoveFromCart()
  const { hapticFeedback } = useTelegram()

  const isTea = item.productType === 'tea'
  const isLoading = updateCartItem.isPending || updateCartItemWeight.isPending || removeFromCart.isPending
  
  // Данные товара
  const productName = item.product?.name || 'Товар'
  const productImage = item.product?.imageUrl || '/placeholder-product.svg'
  const productPrice = item.product?.isOnSale && item.product?.salePriceAmount 
    ? item.product.salePriceAmount 
    : (item.product?.priceAmount || 0)
  
  // Расчёт цены через централизованную функцию
  const priceResult = isTea && item.weight
    ? calculateTeaPrice({
        pricePerGram: item.product?.priceAmount || 0,
        weight: item.weight,
        quantity: item.quantity,
        cakePriceAmount: item.product?.cakePriceAmount,
        cakeWeight: item.product?.cakeWeight,
      })
    : null

  const subtotal = priceResult?.subtotal ?? (productPrice * item.quantity)
  const pricePerGram = productPrice

  const handleQuantityChange = useCallback((delta: number) => {
    const newQuantity = item.quantity + delta
    if (newQuantity < 1) return
    
    hapticFeedback?.impactOccurred('light')
    onHapticFeedback?.('light')
    
    updateCartItem.mutate({
      itemId: item.id,
      quantity: newQuantity,
    })
  }, [item.quantity, item.id, hapticFeedback, onHapticFeedback, updateCartItem])

  const handleWeightChange = useCallback(async (weight: number) => {
    if (!isTea || weight === item.weight) return
    
    hapticFeedback?.impactOccurred('medium')
    
    try {
      await updateCartItemWeight.mutateAsync({
        itemId: item.id,
        weight,
      })
      setIsWeightSheetOpen(false)
    } catch {
      setIsWeightSheetOpen(false)
    }
  }, [isTea, item.weight, item.id, hapticFeedback, updateCartItemWeight])

  const handleRemove = useCallback(() => {
    hapticFeedback?.impactOccurred('medium')
    onHapticFeedback?.('medium')
    removeFromCart.mutate(item.id)
  }, [item.id, hapticFeedback, onHapticFeedback, removeFromCart])

  const handleNavigateToProduct = useCallback(() => {
    hapticFeedback?.impactOccurred('light')
    navigate({ to: '/product/$productId', params: { productId: item.productId } })
  }, [item.productId, hapticFeedback, navigate])

  return (
    <>
      <div className={cn(
        'bg-white dark:bg-dark-card rounded-2xl p-4',
        'border border-silk/50 dark:border-dark-border',
        'transition-opacity duration-200',
        isLoading && 'opacity-60 pointer-events-none'
      )}>
        <div className="flex gap-4">
          {/* Изображение */}
          <button 
            type="button"
            onClick={handleNavigateToProduct}
            className="flex-shrink-0 relative active:scale-[0.98] transition-transform"
          >
            <ProductImage
              src={productImage}
              alt={productName}
              size="md"
              className="rounded-xl w-20 h-20 object-cover"
            />
          </button>

          {/* Информация */}
          <div className="flex-1 min-w-0 flex flex-col">
            <button 
              type="button"
              onClick={handleNavigateToProduct}
              className="text-left"
            >
              <h3 className="text-sm font-semibold text-ink dark:text-white line-clamp-2 leading-tight">
                {productName}
              </h3>
            </button>
            
            {/* Артикул для посуды */}
            {!isTea && item.product?.sku && (
              <p className="text-[10px] font-mono text-gray-400 dark:text-gray-500 mt-0.5">
                Арт. {item.product.sku}
              </p>
            )}
            
            {/* Селектор веса для чая */}
            {isTea && item.weight && (
              <button
                type="button"
                onClick={() => {
                  hapticFeedback?.impactOccurred('light')
                  setIsWeightSheetOpen(true)
                }}
                disabled={isLoading}
                className={cn(
                  'mt-2 self-start inline-flex items-center gap-1.5 px-3 py-1.5',
                  'bg-primary/10 dark:bg-primary/20 rounded-full',
                  'text-xs font-medium text-primary dark:text-primary-light',
                  'active:scale-[0.98] transition-transform duration-100'
                )}
              >
                <Scale className="h-3 w-3" />
                <span>{formatWeight(item.weight)}</span>
                <ChevronDown className="h-2.5 w-2.5 opacity-60" />
              </button>
            )}

            {/* Цена */}
            <div className="mt-auto pt-2">
              <p className="text-base font-bold text-primary dark:text-primary-light">
                {formatPrice(subtotal)}
              </p>
              {isTea && item.weight && priceResult && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {priceResult.isCake 
                    ? `${formatPrice(priceResult.unitPrice)} × ${item.quantity}`
                    : `${formatPrice(pricePerGram)}/г × ${item.weight}г × ${item.quantity}`
                  }
                </p>
              )}
            </div>
          </div>

          {/* Действия */}
          <div className="flex flex-col items-end justify-between">
            {/* Удаление */}
            <button
              type="button"
              onClick={handleRemove}
              disabled={isLoading}
              className={cn(
                'p-2 rounded-xl text-gray-400',
                'hover:text-error hover:bg-error/10',
                'dark:text-gray-500 dark:hover:text-error',
                'active:scale-[0.95] transition-all duration-100'
              )}
              aria-label="Удалить"
            >
              <Trash2 className="h-4 w-4" />
            </button>

            {/* Количество */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-dark-elevated rounded-xl p-1">
              <button
                type="button"
                onClick={() => handleQuantityChange(-1)}
                disabled={isLoading || item.quantity <= 1}
                className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center',
                  'active:scale-[0.9] transition-transform duration-100',
                  'disabled:opacity-40 disabled:scale-100',
                  item.quantity > 1 && 'bg-white dark:bg-dark-card shadow-sm'
                )}
                aria-label="Уменьшить"
              >
                <Minus className="h-4 w-4" />
              </button>

              <span className="w-8 text-center text-sm font-semibold text-ink dark:text-white tabular-nums">
                {item.quantity}
              </span>

              <button
                type="button"
                onClick={() => handleQuantityChange(1)}
                disabled={isLoading}
                className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center',
                  'bg-primary dark:bg-primary-dark text-white shadow-sm',
                  'active:scale-[0.9] transition-transform duration-100',
                  'disabled:opacity-50 disabled:scale-100'
                )}
                aria-label="Увеличить"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Sheet для выбора веса */}
      {isTea && (
        <ProductWeightSelectorSheet
          isOpen={isWeightSheetOpen}
          onClose={() => setIsWeightSheetOpen(false)}
          title="Изменить объём"
          weights={getWeightOptions(
            item.product?.isCakeAvailable ?? false,
            item.product?.isCakeOnly,
            item.product?.cakeWeight,
            item.weight ?? undefined,
          ).map(weight => {
            const result = calculateTeaPrice({
              pricePerGram: item.product?.priceAmount || 0,
              weight,
              quantity: 1,
              cakePriceAmount: item.product?.cakePriceAmount,
              cakeWeight: item.product?.cakeWeight,
            })
            return {
              weight,
              price: result.unitPrice,
              available: true,
            }
          })}
          selectedWeight={item.weight || undefined}
          onSelect={handleWeightChange}
          pricePerGram={pricePerGram}
          cakeWeight={item.product?.cakeWeight}
        />
      )}
    </>
  )
}

export interface CartSummaryProps {
  total: number
  itemCount: number
  onCheckout?: () => void
  loading?: boolean
  onHapticFeedback?: (type?: 'light' | 'medium' | 'heavy') => void
}

/**
 * UI компонент: Итого корзины
 *
 * Reads delivery cost from Zustand delivery store — single source of truth.
 */
export function CartSummary({
  total,
  itemCount,
  onCheckout,
  loading = false,
  onHapticFeedback,
}: CartSummaryProps) {
  const { hapticFeedback } = useTelegram()

  // Delivery state from Zustand store
  const method = useDeliveryStore(s => s.method)
  const cdekInfo = useDeliveryStore(s => s.cdekInfo)
  const deliveryCost = method === 'pickup' ? 0 : (cdekInfo?.cost ?? 0)
  const finalTotal = total + deliveryCost

  const getItemsText = (count: number) => {
    if (count === 1) return 'товар'
    if (count >= 2 && count <= 4) return 'товара'
    return 'товаров'
  }

  // Delivery cost display
  let deliveryLabel: string
  if (method === 'pickup') {
    deliveryLabel = 'Бесплатно (самовывоз)'
  } else if (cdekInfo && deliveryCost > 0) {
    deliveryLabel = `~${formatPrice(deliveryCost)}`
  } else if (method === 'cdek' && !cdekInfo) {
    deliveryLabel = 'выберите пункт СДЭК'
  } else {
    deliveryLabel = 'рассчитается при оформлении'
  }

  return (
    <div className={cn(
      'bg-white dark:bg-dark-card rounded-2xl overflow-hidden',
      'border border-silk/50 dark:border-dark-border'
    )}>
      {/* Хедер */}
      <div className="bg-primary/5 dark:bg-primary/10 px-4 py-3">
        <h2 className="text-lg font-bold text-ink dark:text-white flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          Ваш заказ
        </h2>
      </div>

      <div className="p-4 space-y-4">
        {/* Детализация */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              {itemCount} {getItemsText(itemCount)}
            </span>
            <span className="font-medium text-ink dark:text-white tabular-nums">
              {formatPrice(total)}
            </span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Доставка</span>
            <span className={cn(
              'font-medium tabular-nums',
              method === 'pickup'
                ? 'text-emerald-600 dark:text-emerald-400'
                : deliveryCost > 0
                  ? 'text-ink dark:text-white'
                  : 'text-gray-400 dark:text-gray-500'
            )}>
              {deliveryLabel}
            </span>
          </div>

          <div className="border-t border-silk dark:border-dark-border pt-2 flex justify-between">
            <span className="font-semibold text-ink dark:text-white">Итого</span>
            <span className="text-xl font-bold text-primary dark:text-primary-light tabular-nums">
              {formatPrice(finalTotal)}
            </span>
          </div>
        </div>

        {/* Кнопка */}
        {onCheckout && (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={() => {
              hapticFeedback?.impactOccurred('medium')
              onHapticFeedback?.('medium')
              onCheckout()
            }}
            disabled={loading || itemCount === 0}
            loading={loading}
            className="h-14 rounded-xl"
          >
            Оформить заказ
          </Button>
        )}
      </div>
    </div>
  )
}

export interface EmptyCartProps {
  onNavigateToCatalog: () => void
}

/**
 * UI компонент: Пустая корзина
 */
export function EmptyCart({ onNavigateToCatalog }: EmptyCartProps) {
  const { hapticFeedback } = useTelegram()
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
      <div className="w-24 h-24 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center mb-6">
        <Package className="h-10 w-10 text-primary/60" />
      </div>
      
      <h2 className="text-2xl font-bold text-ink dark:text-white mb-2">
        Корзина пуста
      </h2>
      
      <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-xs">
        Добавьте чай или посуду в корзину, чтобы оформить заказ
      </p>
      
      <Button
        variant="primary"
        size="lg"
        onClick={() => {
          hapticFeedback?.impactOccurred('light')
          onNavigateToCatalog()
        }}
        className="px-8"
      >
        Перейти в каталог
      </Button>
    </div>
  )
}
